import { useEffect, useState } from "react";
import { Box, CircularProgress, DialogTitle } from "@mui/material";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { BASE_URL } from "../../../components/BaseUrl";
import {
  setInviteProfileUpdateToken,
  setInviteEmail as setInviteEmailRedux,
} from "../../../Redux/Slices/AuthSlice";
import warning from "../../../assets/warning.svg";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CustomEmailInput from "../../../components/EmailInput"; // ✅ make sure you import
import { clearInviteSetupComplete } from "../../../Routes/InviteFlowGuard";

const VerifyInvite = () => {
  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("workerId");
  const otpToken = searchParams.get("token");
  const verificationCode = searchParams.get("code");

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true); // for main verify API
  const [resendLoading, setResendLoading] = useState(false); // for resend button only
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const verifyInvite = async () => {
      if (!otpToken || !verificationCode) {
        setErrorMessage("Invalid or missing verification link.");
        setLoading(false);
        return;
      }

      try {
        const url = `${BASE_URL}/public/worker/verify-invite-email`;

        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            otp_token: otpToken,
            verification_code: verificationCode,
          }),
        });

        const data = await response.json();
        console.log("🔍 Worker VerifyInvite response:", data);

        if (!response.ok || data.error) {
          setErrorMessage(data.message || "Invite verification failed");
          return;
        }

        toast.success(data.message || "Invite verified successfully");
        clearInviteSetupComplete();

        if (data.data?.password_token) {
          localStorage.setItem("workerPasswordToken", data.data.password_token);
        }

        const profileToken = data.data?.profileUpdateToken;
        if (profileToken) {
          dispatch(setInviteProfileUpdateToken(profileToken));
          localStorage.setItem("inviteProfileUpdateToken", profileToken);
        }

        if (data.data?.worker?.email) {
          dispatch(setInviteEmailRedux(data.data.worker.email));
          localStorage.setItem("inviteEmail", data.data.worker.email);
        }

        // Save worker_id from URL params for later use
        if (workerId) {
          localStorage.setItem("currentWorkerId", workerId);
          localStorage.setItem("workerId", workerId);
          console.log("💾 Worker ID from URL saved to localStorage:", workerId);
        }

        setTimeout(() => {
          navigate("/update-invite-profile", {
            state: {
              email: data.data?.worker?.email,
              workerId: workerId  // Pass worker_id through navigation state
            },
            replace: true,
          });
        }, 1000);
      } catch (err) {
        console.error("🚨 VerifyInvite catch:", err);
        setErrorMessage(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    verifyInvite();
  }, [otpToken, verificationCode, dispatch, navigate]);

  // 🔁 Resend Worker Invite API
  const handleResendInvite = async () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setResendLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/public/worker/resend-verification-universal`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      console.log("🔁 ResendInvite response:", data);

      if (!response.ok || data.error) {
        throw new Error(data.message || "Failed to resend invite");
      }

      toast.success(data.message || "Invite resent successfully!");
    } catch (err) {
      toast.error(err.message || "Error while resending invite");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#EFF4F8",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      {loading ? (
        <CircularProgress size={40} />
      ) : errorMessage ? (
        <Box
          sx={{
            maxWidth: 420,
            width: "100%",
            bgcolor: "white",
            borderRadius: "12px",
            p: 3,
            textAlign: "center",
          }}
        >
          <DialogTitle sx={{ textAlign: "center" }}>
            <img src={warning} style={{ width: 70, height: 70 }} alt="warning" />
          </DialogTitle>

          <CustomText
            sx={{
              ...textStyles.body4,
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            {errorMessage}
          </CustomText>

          <label
            style={{
              display: "block",
              textAlign: "left",
              marginBottom: 6,
              fontSize: 14,
              fontWeight: 500,
              color: theme.palette.text.primary,
            }}
          >
            {t("invite.emailLabel")}
          </label>
          <CustomEmailInput
            name="email"
            placeholder="john@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <CustomButton
            onClick={handleResendInvite}
            disabled={resendLoading}
            sx={{ mt: 2 }}
            label={
              resendLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                  {t("invite.resending")}
                </Box>
              ) : (
                t("invite.resendButton")
              )
            }
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default VerifyInvite;


